<template>
  <div class="flex items-start gap-2">
    <div class="min-w-0 grow" ref="conditionEl">
      <div class="flex flex-wrap items-stretch gap-2" :key="integratedPartner">
        <div
          v-if="isDisconnectedSource"
          class="flex grow items-start gap-2 rounded-sm border border-red-400 bg-red-50 px-3 py-2 text-sm text-red-700">
          <ExclamationTriangleIcon class="mt-0.5 h-5 w-5 shrink-0" />
          <span>
            This rule references
            <strong>{{ disconnectedSourceLabel }}</strong>
            , which isn't a connected integration. It's safely ignored at runtime — no visitors will match it, and your
            other rules still work normally. Remove it with the
            <strong>X</strong>
            , or reconnect {{ disconnectedSourceLabel }} to use it again.
          </span>
        </div>

        <ConditionTriggerPicker
          v-else
          v-model="combinedSourceType"
          :groups="resolvedSourceFields"
          aria-label="Condition trigger"
          placeholder="Select A Trigger">
          <template #value="{ value, placeholder }">
            <div v-if="!value" class="text-sm">
              {{ placeholder }}
            </div>
            <div v-else class="flex items-center gap-2">
              <component
                v-if="selectedSourceOption?.questionType"
                :is="questionTypeIcon(selectedSourceOption.questionType)"
                class="h-4 w-4"
                aria-hidden="true" />
              <div class="text-xs opacity-70">{{ sourceLabel }}</div>
              <div class="text-sm">{{ sourceValue }}</div>
            </div>
          </template>
        </ConditionTriggerPicker>
        <AnswerConditionEditor
          v-if="localCondition.$source === 'answers'"
          :condition="localCondition"
          @update:condition="localCondition = $event" />

        <template v-if="!isDisconnectedSource" v-for="field in availableFields" :key="field.key">
          <Select
            v-if="field.control === 'select'"
            v-model="localCondition[field.name]"
            :options="typeof field.options === 'function' ? field.options(localCondition) : field.options"
            optionLabel="label"
            optionValue="value"
            :autoOptionFocus="true" />
          <ConditionFieldInput
            v-if="field.control === 'text' || field.control === 'number'"
            :condition="localCondition"
            :field="field"
            :field-error="fieldErrors[field.name]"
            @input="debouncedValidateField"
            @blur="validateField" />
          <MultiSelect
            v-if="field.control === 'multiselect'"
            v-model="localCondition[field.name]"
            :options="typeof field.options === 'function' ? field.options(localCondition) : field.options"
            optionLabel="label"
            optionValue="value"
            display="chip"
            filter
            filterPlaceholder="Search..." />
          <ConditionOptionPicker
            v-if="field.control === 'autocomplete'"
            v-model="localCondition[field.name]"
            :options="field.options"
            :multiple="field.multiple"
            :placeholder="field.placeholder"
            :aria-label="field.placeholder" />
          <SyncUpField
            v-if="field.control === 'customField'"
            v-model="localCondition[field.name]"
            :label="false"
            :prefix="false"
            :include-native="false"
            :can-clear="false"
            :creatable="false"
            aria-label="Custom field"
            placeholder="Choose a field" />
          <!-- DatePicker parses string models with its own mm/dd/yy format, so it must receive a Date. -->
          <DatePicker
            v-if="field.control === 'date'"
            :modelValue="parseConditionDate(localCondition[field.name])"
            @update:modelValue="localCondition[field.name] = $event instanceof Date ? $event.toISOString() : $event"
            :showTime="field.timeOnly"
            :timeOnly="field.timeOnly" />
          <div v-if="field.control === 'content'" class="flex items-center">
            {{ field.content }}
          </div>
        </template>
      </div>
    </div>
    <button
      type="button"
      class="mt-1.5 inline-flex size-7 shrink-0 cursor-pointer items-center justify-center rounded-md text-faint transition-colors duration-150 hover:bg-overlay hover:text-heading focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-primary"
      aria-label="Remove condition"
      title="Remove"
      @click="deleteCondition">
      <XMarkIcon class="size-3.5" aria-hidden="true" />
    </button>
  </div>
</template>

<script setup>
import { XMarkIcon, ExclamationTriangleIcon } from '@heroicons/vue/24/outline'
import { makeSchema, makeSourceFields } from '@dashboard/modules/segment/data/logicDefinitionSchema'
import { FilterMatchMode, FilterService } from '@primevue/core/api'
import ConditionOptionPicker from './ConditionOptionPicker.vue'
import SyncUpField from '@dashboard/components/common/SyncUpField.vue'
import { useIntegrationStore } from '@dashboard/modules/integration/stores/integrationStore'
import ConditionFieldInput from '@dashboard/modules/segment/components/ConditionFieldInput.vue'
import AnswerConditionEditor from './AnswerConditionEditor.vue'
import { validateAnswerCondition } from '../services/answerConditions'
import { parseConditionDate } from '../services/conditionDates'
import { questionTypeIcon } from './question/questionCard'
import { useDebounceFn } from '@vueuse/core'
import { storeToRefs } from 'pinia'
import ConditionTriggerPicker from './ConditionTriggerPicker.vue'
import DatePicker from 'primevue/datepicker'
import MultiSelect from 'primevue/multiselect'
import Select from 'primevue/select'
import { computed, inject, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'

const props = defineProps({
  condition: Object,
  widgetTargeting: { type: Boolean, default: false },
  sourceFields: { type: Array, default: null },
  formSchema: { type: Array, default: null }
})

const emit = defineEmits(['update:condition', 'delete:condition'])
const route = useRoute()

const conditionEl = ref(null)
const localCondition = ref(props.condition)
let replacementSourceType
watch(
  () => props.condition,
  (condition) => {
    if (props.widgetTargeting) {
      replacementSourceType =
        condition.$source === 'answers'
          ? `answers*${condition.questionId}`
          : condition.$source && condition.$type
            ? `${condition.$source}*${condition.$type}`
            : null
      localCondition.value = condition
    }
  }
)
const fieldErrors = ref({}) // Track validation errors for each field

watch(
  localCondition,
  (newValue) => {
    // Keep incomplete previous-answer edits local instead of autosaving invalid leaves.
    if (newValue.$source === 'answers' && validateAnswerCondition(newValue).length > 0) {
      return
    }
    emit('update:condition', newValue)
  },
  { deep: true }
)

const segmentGroup = inject('segmentGroup', null)
const logicDefinitionContext = computed(() => ({
  ignoreSegmentGroups: segmentGroup ? [segmentGroup.value] : [],
  usageContext: props.widgetTargeting ? 'widget-targeting' : undefined,
  offerFunnelId: Array.isArray(route.params.offerFunnelId) ? route.params.offerFunnelId[0] : route.params.offerFunnelId
}))

const integrationStore = useIntegrationStore()
const { integratedPartner, partnerData, partners } = storeToRefs(integrationStore)

const deleteCondition = () => {
  emit('delete:condition')
}

const combinedSourceType = computed({
  get: () => {
    if (localCondition.value.$source === 'answers') {
      return `answers*${localCondition.value.questionId}`
    }
    if (localCondition.value.$source && localCondition.value.$type) {
      return `${localCondition.value.$source}*${localCondition.value.$type}`
    }
    return null
  },
  set: (newValue) => {
    const [source, type] = newValue.split('*')
    if (source === 'answers') {
      localCondition.value = { $source: 'answers', questionId: type, operator: 'has answered' }
      return
    }
    if (localCondition.value.$source === 'answers') {
      localCondition.value = { $source: source, $type: type }
      return
    }
    localCondition.value.$source = source
    localCondition.value.$type = type
  }
})

const resetFieldValue = (field) => {
  if (field.control === 'select') {
    const options = typeof field.options === 'function' ? field.options(localCondition.value) : field.options
    if (options && options.length > 0) {
      localCondition.value[field.name] = options[0].value
    }
  } else if (field.control === 'multiselect' || (field.control === 'autocomplete' && field.multiple)) {
    localCondition.value[field.name] = []
  } else if (field.control === 'autocomplete' || field.control === 'customField') {
    localCondition.value[field.name] = undefined
  } else {
    localCondition.value[field.name] = ''
  }
}

watch(combinedSourceType, (newValue) => {
  if (props.widgetTargeting && newValue === replacementSourceType) {
    replacementSourceType = undefined
    return
  }
  replacementSourceType = undefined
  // Clear validation errors when changing condition type
  fieldErrors.value = {}

  availableFields.value.forEach(resetFieldValue)
})

// Watch for occurrence changes to set defaults
watch(
  () => localCondition.value.occurrence,
  (newOccurrence) => {
    if (newOccurrence === 'any') {
      // Set default values for the new flexible format
      if (!localCondition.value.occurrenceOperator) {
        localCondition.value.occurrenceOperator = 'gte' // Default to 'at least'
      }
      if (!localCondition.value.occurrenceCount) {
        localCondition.value.occurrenceCount = 1
      }
      if (!localCondition.value.timeframe) {
        localCondition.value.timeframe = 'ever' // Default to 'ever'
      }
    } else if (newOccurrence === 'not_any') {
      // For "Has not visited", set timeframe default
      if (!localCondition.value.timeframe) {
        localCondition.value.timeframe = 'ever' // Default to 'ever'
      }
      // Clear operator and count as they're not used for "not_any"
      delete localCondition.value.occurrenceOperator
      delete localCondition.value.occurrenceCount
    }
  }
)

// Watch for timeframe changes to set duration/unit defaults
watch(
  () => localCondition.value.timeframe,
  (newTimeframe) => {
    if (newTimeframe === 'within' || newTimeframe === 'outside') {
      // Set default duration and unit if not already set
      if (!localCondition.value.duration) {
        localCondition.value.duration = 1
      }
      if (!localCondition.value.unit) {
        localCondition.value.unit = 'week'
      }
    }
  }
)

const selectedSourceGroup = computed(() =>
  resolvedSourceFields.value.find((field) => field.options.some((option) => option.value === combinedSourceType.value))
)
const selectedSourceOption = computed(() =>
  selectedSourceGroup.value?.options.find((option) => option.value === combinedSourceType.value)
)
const sourceLabel = computed(
  () => selectedSourceGroup.value?.group || (localCondition.value.$source === 'answers' ? 'Segments / Questions' : '')
)
const sourceValue = computed(
  () =>
    selectedSourceOption.value?.label ||
    (localCondition.value.$source === 'answers' ? 'Question no longer available' : '')
)

const resolvedSourceFields = computed(() => {
  if (props.sourceFields) {
    return props.sourceFields
  }

  return makeSourceFields({
    partner: integratedPartner.value,
    context: logicDefinitionContext.value
  })
})

// Flag conditions whose $source isn't available under the current partner —
// e.g., stale `$source: 'drip'` rules left behind after a team disconnects
// Drip and moves to Kit. Surfaces the same way field-level validation does
// (red border + tooltip) so the user can see why the condition looks blank
// and click the X to remove it. We deliberately DO NOT auto-delete these in
// a repair() hook — that would silently destroy user-authored rules if the
// integration is only temporarily disconnected (expired OAuth, mid-migration).
const isDisconnectedSource = computed(() => {
  const source = localCondition.value.$source
  if (!source || source === 'answers') {
    return false
  }
  return !resolvedSourceFields.value.some((f) => f.name === source)
})

// Resolve the human-readable label for the disconnected source (e.g. "drip" → "Drip")
// by looking it up in the full partners list from the integration store. Falls back
// to the raw internal name if the partner isn't in the list (e.g., deprecated platform).
const disconnectedSourceLabel = computed(() => {
  const source = localCondition.value.$source
  if (!source) {
    return ''
  }
  return partners.value?.find((p) => p.name === source)?.label || source
})

const availableFields = computed(() =>
  resolvedControlSchema.value.filter((field) => {
    if (!field.can) {
      return false
    }
    return typeof field.can === 'function' ? field.can(localCondition.value) : field.can
  })
)

const resolvedControlSchema = computed(() => {
  if (props.formSchema) {
    return props.formSchema
  }

  return makeSchema({
    partner: integratedPartner.value,
    context: logicDefinitionContext.value
  })
})

watch(
  () => {
    const field = availableFields.value.find((field) => field.name === 'operator' && field.control === 'select')
    return field && (typeof field.options === 'function' ? field.options(localCondition.value) : field.options)
  },
  (options) => {
    if (!options?.length || options.some((option) => option.value === localCondition.value.operator)) {
      return
    }

    const previousValueField = availableFields.value.find((field) => field.name === 'value')
    localCondition.value.operator = options[0].value
    delete fieldErrors.value.operator
    const valueField = availableFields.value.find((field) => field.name === 'value')
    if (
      valueField &&
      (valueField.control !== previousValueField?.control || valueField.multiple !== previousValueField?.multiple)
    ) {
      resetFieldValue(valueField)
      delete fieldErrors.value.value
    }
  }
)

watch(
  availableFields,
  (fields) => {
    fields.forEach((field) => {
      const value = localCondition.value[field.name]
      if (field.control === 'multiselect' && (value === '' || value === null || value === undefined)) {
        localCondition.value[field.name] = []
      }
    })
  },
  { immediate: true }
)

// TODO: Refactor

// const customFields = getIntegrationData('custom_fields', (field) => ({
//   label: field.label,
//   value: String(field.id)
// }))

// const tags = getIntegrationData('tags', (tag) => ({
//   label: tag.label,
//   value: String(tag.id)
// }))

// const lists = getIntegrationData('lists', (list) => ({
//   label: list.label,
//   value: String(list.id)
// }))

const dataMappings = {
  customFields: [],
  tags: [],
  lists: []
}

const dataSearch = (field, event) => {
  const query = event.query
  const data = dataMappings[field.type]
  if (!query) {
    return data.value
  }
  field.suggestions = data.value.filter((item) => item.label.toLowerCase().includes(query.toLowerCase()))
}

/* Actions */

const validateField = (field, value) => {
  // Only validate if the field has a validate function
  if (field.validate && typeof field.validate === 'function') {
    // Pass both the value and the entire condition object for context-aware validation
    const error = field.validate(value, localCondition.value)
    if (error) {
      fieldErrors.value[field.name] = error
    } else {
      delete fieldErrors.value[field.name]
    }
  }
}

// Debounced validation to prevent focus loss during typing
// Validation runs 300ms after typing stops, but immediate on blur
const debouncedValidateField = useDebounceFn((field, value) => {
  validateField(field, value)
}, 300)

const specialFieldChange = (newValue) => {
  const [source, type] = newValue.split('*')
  const fields = Object.keys(localCondition.value)
  fields.forEach((field) => delete localCondition.value[field])
  localCondition.value.$source = source
  localCondition.value.$type = type
  // Clear any validation errors when changing source/type
  fieldErrors.value = {}
}

// Validate fields on mount. Runs unconditionally (including on blank values)
// because some validators flag empty input as the error — e.g. custom-field
// value inputs with `equals` operator, which the snippet short-circuits to
// false at runtime and thus can never match.
onMounted(() => {
  availableFields.value.forEach((field) => {
    if (field.validate) {
      validateField(field, localCondition.value[field.name])
    }
  })
})

// Revalidate when operator changes
watch(
  () => localCondition.value.operator,
  () => {
    const valueField = availableFields.value.find((field) => field.name === 'value')
    if (valueField && valueField.validate) {
      validateField(valueField, localCondition.value.value)
    }
  }
)

// onUpdated(() => {
//   const el = conditionEl.value
//   el.querySelectorAll('input').forEach((input) => {
//     input.style.width = input.value.length + 'ch'
//   })
// })
</script>
